// Get the secret key from environment variables.
//
// NOTE:
// - In Vite runtime, `import.meta.env` exists, but Jest (CJS) cannot parse `import.meta`.
// - To keep this module testable in Jest and still configurable in CI, we read from `process.env`.
//   In the browser build, you can inject `VITE_ENCRYPTION_SECRET` at build time via env tooling.
const SECRET_KEY =
  (typeof process !== 'undefined' && process.env && process.env.VITE_ENCRYPTION_SECRET) ||
  'pharm-connect-default-key-2024';

/**
 * Encrypt a conversation ID for URL usage
 * @param conversationId - The conversation ID to encrypt
 * @returns Encrypted string safe for URL usage
 */
export const encryptId = (conversationId: string): string => {
  try {
    // Simple XOR encryption with the secret key (for compatibility)
    let encrypted = '';
    for(let i = 0; i < conversationId.length; i++){
      const charCode = conversationId.charCodeAt(i);
      const keyChar = SECRET_KEY.charCodeAt(i % SECRET_KEY.length);
      encrypted += String.fromCharCode(charCode ^ keyChar);
    }
    // Convert to base64 and make URL-safe (same as frontend)
    const base64 = btoa(encrypted);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error encrypting ID:', error);
    throw new Error('Failed to encrypt conversation ID');
  }
};

/**
 * Decrypt a conversation ID from URL
 * @param encryptedId - The encrypted ID from URL
 * @returns Decrypted conversation ID
 */
export const decryptId = (encryptedId: string): string => {
  try {
    // Convert from URL-safe base64 back to regular base64
    const base64 = encryptedId.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
    // Convert base64 to string
    const encrypted = atob(paddedBase64);
    // Simple XOR decryption with the secret key
    let decrypted = '';
    for(let i = 0; i < encrypted.length; i++){
      const charCode = encrypted.charCodeAt(i);
      const keyChar = SECRET_KEY.charCodeAt(i % SECRET_KEY.length);
      decrypted += String.fromCharCode(charCode ^ keyChar);
    }
    if (!decrypted) {
      throw new Error('Invalid encrypted ID');
    }
    return decrypted;
  } catch (error) {
    console.error('Error decrypting ID:', error);
    throw new Error('Failed to decrypt conversation ID');
  }
};

/**
 * Validate if a string is a valid encrypted ID format
 * @param encryptedId - The encrypted ID to validate
 * @returns True if the format looks valid
 */
export const isValidEncryptedId = (encryptedId: string): boolean => {
  try {
    // Basic format validation - should be base64-like without padding
    return /^[A-Za-z0-9_-]+$/.test(encryptedId) && encryptedId.length > 10;
  } catch {
    return false;
  }
};
